import { createContext, useReducer, useCallback, useRef } from "react";

import axios from "../../utils/axios/v1/userAxios";

const INITIALIZE = "INITIALIZE";
const GENERATE_CERT = "GENERATE_CERT";
const VERIFY_DOMAINS = "VERIFY_DOMAINS";
const CLEAR_WILDCARD_CERTS = "CLEAR_WILDCARD_CERTS";
const GET_SSL_CONFIG = "GET_SSL_CONFIG";
const GET_HSTS_ENABLED = "GET_HSTS_ENABLED";
const SET_ERROR = "SET_ERROR";
const SET_SSL_RATING = "SET_SSL_RATING";
const SET_SSL_RATING_LOADING = "SET_SSL_RATING_LOADING";
const SET_SSL_RATING_PROGRESS = "SET_SSL_RATING_PROGRESS";

const initialState = {
  cert_id: "",
  cname_validations: [],
  sslConfig: null,
  errMsg: null,
  sslRating: null,
  sslRatingLoading: false,
  sslRatingProgress: null,
};

const SslConfigReducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        ...state,
        cert_id: action.payload.cert_id,
        cname_validations: action.payload.cname_validations,
      };
    case GENERATE_CERT:
      return {
        ...state,
        cert_id: action.payload.cert_id,
        cname_validations: action.payload.cname_validations,
      };
    case CLEAR_WILDCARD_CERTS:
      return {
        ...state,
        cert_id: "",
        cname_validations: [],
      };
    case VERIFY_DOMAINS:
      return {};
    case GET_SSL_CONFIG:
      return {
        ...state,
        sslConfig: action.payload.sslConfig,
      };
    case GET_HSTS_ENABLED: {
      const { sslConfig } = state;
      const hsts_enabled = action.payload.hsts_enabled;
      if (sslConfig.hsts) {
        sslConfig.hsts.enabled = hsts_enabled;
      } else {
        sslConfig.hsts = { enabled: hsts_enabled };
      }
      return {
        ...state,
        sslConfig,
      };
    }
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    case SET_SSL_RATING:
      return {
        ...state,
        sslRating: action.payload.sslRating,
      };
    case SET_SSL_RATING_LOADING:
      return {
        ...state,
        sslRatingLoading: action.payload.loading,
      };
    case SET_SSL_RATING_PROGRESS:
      return {
        ...state,
        sslRatingProgress: action.payload.progress,
      };
    default:
      return state;
  }
};

const SSLConfigContext = createContext(null);

function SslConfigProvider({ children }) {
  const [state, dispatch] = useReducer(SslConfigReducer, initialState);
  const pollingIntervalRef = useRef(null);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const setSSLConfig = useCallback((sslConfig) => {
    dispatch({
      type: GET_SSL_CONFIG,
      payload: {
        sslConfig: sslConfig,
      },
    });
  }, []);
  const getSSLConfig = useCallback(
    async (siteUid, init = false) => {
      if (init) {
        setSSLConfig(null);
      }
      try {
        const response = await axios.get(`config/ssl/${siteUid}`);
        setSSLConfig(response.data);
        return { status: "success", data: response.data };
      } catch (err) {
        setSSLConfig([]);
        setErr(err.message);
        return { status: "fail", data: err.message };
      }
    },
    [setSSLConfig, setErr]
  );

  const generateCerts = useCallback(
    async (siteUid) => {
      const res = await axios.post(`/config/ssl/${siteUid}/generate_certs`, {
        siteUid,
      });
      let cname_validations = [];
      cname_validations.push(res.data.cname_validation_p1);
      cname_validations.push(res.data.cname_validation_p2);
      let cert_id = res.data.id;
      dispatch({
        type: GENERATE_CERT,
        payload: {
          cert_id,
          cname_validations,
        },
      });
      getSSLConfig(siteUid);
      return res.data;
    },
    [getSSLConfig]
  );

  const verifyDomains = useCallback(async (siteUid, cert_id) => {
    await axios.post(`/config/ssl/${siteUid}/verify_domains`, {
      cert_id,
    });
  }, []);

  const clearWildcardCerts = useCallback(() => {
    dispatch({
      type: CLEAR_WILDCARD_CERTS,
    });
  }, []);

  const sslTypeChange = useCallback(
    async (siteUid, sslType) => {
      try {
        const res = await axios.patch(`/config/ssl/${siteUid}`, {
          ssl_type: sslType,
        });
        setSSLConfig(res.data);
      } catch (err) {
        setErr(err.message);
        getSSLConfig(siteUid, true);
      }
    },
    [setSSLConfig, setErr, getSSLConfig]
  );
  const configSslSetting = useCallback(
    async (siteUid, name, value) => {
      try {
        let res;
        switch (name) {
          case "wwwRedirectChange":
            {
              // Combined toggle + direction. When `direction` is
              // supplied, the backend writes the smallint Layerd
              // actually reads (www_redirect_type) and keeps the
              // boolean coherent. When omitted, backend defaults to
              // Apex → www on first enable.
              const payload = { www_redirect_enabled: value.enable };
              if (value.direction !== undefined && value.direction !== null) {
                payload.www_redirect_type = value.direction;
              }
              res = await axios.patch(`/config/ssl/${siteUid}`, payload);
              setSSLConfig(res.data);
            }
            break;
          case "tlsVersionChange":
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              min_tls_version: value.version,
            });
            setSSLConfig(res.data);
            break;
          case "tlsProfileChange":
            {
              // Profile is the driver. intermediate/modern preset →
              // backend expands to the four locked columns atomically.
              // custom → operator-supplied min_tls_version (optional max
              // forwarded too) take effect with the per-column rules
              // enforced server-side.
              const payload = { tls_policy_preset: value.preset };
              if (value.preset === "custom") {
                if (value.min_tls_version !== undefined) {
                  payload.min_tls_version = value.min_tls_version;
                }
                if (value.max_tls_version !== undefined) {
                  payload.max_tls_version = value.max_tls_version;
                }
                // Empty array → null (= inherit listener default).
                // Backend Joi rejects a non-null cipher_list with <4 entries
                // and any token outside the catalog, so the UI doesn't
                // need to guard those — let the operator see the real
                // error instead of pre-empting it.
                if (value.cipher_list !== undefined) {
                  payload.cipher_list =
                    value.cipher_list && value.cipher_list.length > 0
                      ? value.cipher_list.join(":")
                      : null;
                }
                if (value.ciphersuites !== undefined) {
                  payload.ciphersuites =
                    value.ciphersuites && value.ciphersuites.length > 0
                      ? value.ciphersuites.join(":")
                      : null;
                }
              }
              res = await axios.patch(`/config/ssl/${siteUid}`, payload);
              setSSLConfig(res.data);
            }
            break;
          case "httpsRedirectChange":
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              https_redirect_enabled: value.enable,
            });
            setSSLConfig(res.data);
            break;
          case "autoHttpRewriteChange":
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              http_rewrite_enabled: value.enable,
            });
            setSSLConfig(res.data);
            break;
          case "hstsSettingChange":
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              hsts: { enabled: value.enable },
            });
            dispatch({
              type: GET_HSTS_ENABLED,
              payload: { hsts_enabled: value.enable },
            });
            setSSLConfig(res.data);
            break;
          case "uploadCert":
            delete value.site_id;
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              certs: value,
            });
            setSSLConfig(res.data);
            break;
          case "HSTSUpdate":
            res = await axios.patch(`/config/ssl/${siteUid}`, {
              hsts: value,
            });
            setSSLConfig(res.data);
            break;
          default:
            break;
        }
        // getSSLConfig(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        if (name !== "uploadCert") getSSLConfig(siteUid, true);
        return false;
      }
    },
    [setSSLConfig, setErr, getSSLConfig]
  );
  const getOriginCert = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.get(`/config/ssl/${siteUid}/sg_certs`);
        return response.data;
      } catch (err) {
        setErr(err.message);
      }
    },
    [setErr]
  );
  const setOriginCert = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.post(`/config/ssl/${siteUid}/generate_sg_certs`, {
          subdomains: [],
        });
        await getSSLConfig(siteUid);
        return response.data;
      } catch (err) {
        setErr(err.message);
      }
    },
    [setErr, getSSLConfig]
  );

  const setSSLRating = useCallback((rating) => {
    dispatch({
      type: SET_SSL_RATING,
      payload: { sslRating: rating },
    });
  }, []);

  const setSSLRatingLoading = useCallback((loading) => {
    dispatch({
      type: SET_SSL_RATING_LOADING,
      payload: { loading },
    });
  }, []);

  const setSSLRatingProgress = useCallback((progress) => {
    dispatch({
      type: SET_SSL_RATING_PROGRESS,
      payload: { progress },
    });
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const getLatestSSLRating = useCallback(
    async (siteUid, enablePolling = true) => {
      try {
        const response = await axios.get(`config/ssl_rating/${siteUid}/latest`);
        const data = response.data;
        setSSLRating(data);

        // If scan is in progress and polling is enabled, start polling
        if (data.status === "in_progress" && enablePolling) {
          stopPolling();
          setSSLRatingLoading(true);
          setSSLRatingProgress(data.progress || 0);

          pollingIntervalRef.current = setInterval(async () => {
            try {
              const pollResponse = await axios.get(`config/ssl_rating/${siteUid}/latest`);
              const pollData = pollResponse.data;
              setSSLRating(pollData);

              if (pollData.status === "ready" || pollData.status === "error" || pollData.status === "not_available") {
                stopPolling();
                setSSLRatingLoading(false);
                setSSLRatingProgress(null);
              } else if (pollData.status === "in_progress") {
                setSSLRatingProgress(pollData.progress || 0);
              }
            } catch (pollErr) {
              stopPolling();
              setSSLRatingLoading(false);
              setSSLRatingProgress(null);
            }
          }, 30000); // Poll every 30 seconds as per API docs
        } else {
          // Status is not in_progress, ensure loading state is cleared
          setSSLRatingLoading(false);
          setSSLRatingProgress(null);
        }

        return data;
      } catch (err) {
        if (err.response?.status !== 404) {
          setErr(err.message);
        }
        setSSLRating(null);
        setSSLRatingLoading(false);
        setSSLRatingProgress(null);
        return null;
      }
    },
    [setSSLRating, setErr, stopPolling, setSSLRatingLoading, setSSLRatingProgress]
  );

  const getSSLRatingHistory = useCallback(
    async (siteUid, limit = 10) => {
      try {
        const response = await axios.get(`config/ssl_rating/${siteUid}?limit=${limit}`);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return [];
      }
    },
    [setErr]
  );

  // Cert provider listing — feeds the provisioning wizard's provider
  // picker. Returns one entry per provider (zerossl/acm/digicert/custom/
  // origin) with enabled + remaining-quota.
  const getCertProviders = useCallback(async () => {
    try {
      const r = await axios.get("/config/ssl/providers");
      return { status: "success", data: r.data };
    } catch (err) {
      setErr(err.message);
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, [setErr]);

  // Provisioning lifecycle — one method per route under /:siteUid/provision*.
  const provisionSubmit = useCallback(async (siteUid, body) => {
    try {
      const r = await axios.post(`/config/ssl/${siteUid}/provision`, body);
      return { status: "success", data: r.data };
    } catch (err) {
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, []);
  const provisionStatus = useCallback(async (siteUid) => {
    try {
      const r = await axios.get(`/config/ssl/${siteUid}/provision/status`);
      return { status: "success", data: r.data };
    } catch (err) {
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, []);
  const provisionVerify = useCallback(async (siteUid) => {
    try {
      const r = await axios.post(`/config/ssl/${siteUid}/provision/verify`);
      return { status: "success", data: r.data };
    } catch (err) {
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, []);
  const provisionCancel = useCallback(async (siteUid) => {
    try {
      const r = await axios.delete(`/config/ssl/${siteUid}/provision`);
      return { status: "success", data: r.data };
    } catch (err) {
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, []);
  const provisionInstall = useCallback(async (siteUid, opts = {}) => {
    try {
      const body = opts.force ? { force: true } : undefined;
      const r = await axios.post(`/config/ssl/${siteUid}/provision/install`, body);
      return { status: "success", data: r.data };
    } catch (err) {
      return { status: "fail", data: err?.response?.data || err.message };
    }
  }, []);

  // Account-wide SSL/TLS overview — pulls per-site cert details + aggregate
  // counters + 24h TLS-version traffic share. The CP filters by project ACL,
  // so the caller just passes the active project_id (or undefined for all).
  const getSSLOverview = useCallback(
    async (project_id, listOpts = null) => {
      try {
        const params = {};
        if (project_id && project_id !== "all") params.project_id = project_id;
        if (listOpts && typeof listOpts === "object") {
          if (listOpts.page !== undefined) params.page = listOpts.page;
          if (listOpts.limit !== undefined) params.limit = listOpts.limit;
          if (listOpts.q) params.q = listOpts.q;
          if (listOpts.filter && listOpts.filter !== "ALL") params.filter = listOpts.filter;
        }
        const response = await axios.get("/config/ssl/overview", {
          params: Object.keys(params).length ? params : undefined,
        });
        return { status: "success", data: response.data };
      } catch (err) {
        setErr(err.message);
        return { status: "fail", data: err?.response?.data || err.message };
      }
    },
    [setErr]
  );

  return (
    <SSLConfigContext.Provider
      value={{
        ...state,
        clearWildcardCerts,
        generateCerts,
        verifyDomains,
        getSSLConfig,
        getSSLOverview,
        getCertProviders,
        provisionSubmit,
        provisionStatus,
        provisionVerify,
        provisionCancel,
        provisionInstall,
        getOriginCert,
        setOriginCert,
        sslTypeChange,
        configSslSetting,
        setErr,
        getLatestSSLRating,
        getSSLRatingHistory,
        stopPolling,
      }}
    >
      {children}
    </SSLConfigContext.Provider>
  );
}

export { SSLConfigContext, SslConfigProvider };
